import {DtWs} from "../net/DtWebSocket";
import {DtConstants} from "./schema/DtConstants";
import {endpoint} from "../net/endpoints";

const WebSocket = require('isomorphic-ws');

const DT_MSG_CLIENT_RECREATE = 4001;

const SILENCED = new Set([DtConstants.ChangeTypes.UpdateIot]);

//DT web socket protocol.
class DtMsgWs extends DtWs {

	constructor(loadContext, errorCB, sessionId, onReconnect = () => {
	}) {

		super("msg_ws", loadContext, null, errorCB);

		this.sessionId = sessionId;
		this.changeSubscribers = {};
		this.onReconnect = onReconnect;
		this.pendingReconnect = null;
		this.lastMessageReceivedAt = {};
		this.msg_ws_endpoint = loadContext.msg_ws;
	}

	// if this method gets called multiple times,
	// later subscribers just take over.
	// This happens when a DtModel gets loaded for instance.
	// TODO consider allowing multiple callbacks per modelId,
	// plus unsubscribe for making things more obvious
	subscribeForChanges(targetId, cb) {

		//console.log("Subscribing for", targetId);

		if (!this._wsUsable || !cb) {
			return;
		}

		const prevSubscriber = this.changeSubscribers[targetId];

		this.changeSubscribers[targetId] = cb;

		if (!prevSubscriber) {
			//If we don't have a web socket yet, the subscription
			//will happen in onOpen().
			const ws = this.getWebSocket();
			if (ws) {
				const ts = this.lastMessageReceivedAt[targetId]/* || Math.floor(new Date().getTime()) - 60 * 60 * 1000*/;

				if (!ts) {
					// TODO: uncomment to enable replay events on reconnect
					this.lastMessageReceivedAt[targetId] = new Date().getTime();
				}

				ws.send(`/subscribe/${targetId}${
						ts > 0
							? "/offlinesince/" + ts
							: ""
					}`
				);
			}
		}
	}

	unsubscribeFromChanges(targetId) {
		console.log("Unsubscribing from", targetId);
		const ws = this.getWebSocket();
		if (ws) {
			ws.send(`/unsubscribe/${targetId}`);
		}

		delete (this.lastMessageReceivedAt[targetId]);
		delete (this.changeSubscribers[targetId]);
	}

	isSubscribed(targetId) {
		return !!this.changeSubscribers[targetId];
	}

	get numSubscribers() {
		return Object.keys(this.changeSubscribers).length;
	}

	replayOnReconnect() {
		const ws = this.getWebSocket();
		if (ws?.readyState === WebSocket.OPEN) {
			this.subscribeTargets(true)
		} else {
			this._pendingCallbacks.push(() => this.subscribeTargets(true));
		}
	}

	subscribeTargets(isReplayOnReconnect = false) {
		for (let targetId in this.changeSubscribers) {
			const ts = this.lastMessageReceivedAt[targetId]/*(new Date().getTime()) - 60 * 60 * 1000*/;
			// const ts = (new Date().getTime()) - 60 * 60 * 1000;
			this.ws.send(`/subscribe/${targetId}${isReplayOnReconnect ? "/offlinesince/" + ts : ""}`);

			if (!ts) {
				this.lastMessageReceivedAt[targetId] = new Date().getTime();
			}
		}
	}

	onOpen(event) {
		super.onOpen(event);
		this.subscribeTargets();
	}

	onMessage(data) {
		let change;
		try {
			change = JSON.parse(data);
		} catch (err) {
			console.error(`Failed to parse change notification ${data}`, err);
			return;
		}

		if (this.sessionId && change.sessionId && this.sessionId === change.sessionId) {
			// This is the change notification, originating from the same browser session
			change.isOwn = true;
		}
		// Now we need to route the change to the proper model
		//console.log('Change received', change);
		let targetId = change.modelId || change.twinId || change.groupId;
		// TODO: uncomment to enable replay events on reconnect
		this.lastMessageReceivedAt[targetId] = new Date().getTime();

		// No invalidation or OT rebuild required if it's from a local change
		if (change.isOwn) {
			const reducedEvent = {...change, changedElements: `[${change.changedElements?.length}]`};
			console.info("ignore notification about own change", reducedEvent);
			return;
		} else {
			if (!SILENCED.has(change.ctype)) {
				console.info("received remote event:", change.ctype);
			}
		}

		let cb = this.changeSubscribers[targetId];
		cb && cb(change);
		if (!cb) {
			console.warn(`${targetId} is not subscribed`);
		}
	}

	onClose(event) {
		console.log("DTMSG CLOSED:", event);
		// https://developer.mozilla.org/en-US/docs/Web/API/CloseEvent#status_codes
		const reconnectOn = [1006, 1012, 1013, 1014];

		if (reconnectOn.includes(event.code)) {
			this.reconnect();
		}
	}

	onError(event) {
		this.pendingReconnect = false;
		console.log("DTMSG ERR:", event);
		this.reconnect();
	}

	reconnect(immediate = false) {
		if (this.ws?.readyState === WebSocket.OPEN) {
			// already reconnected, do nothing - it could happen if one of the sockets has lost connection, but the other one is still up
			return;
		}
		if (!navigator?.onLine) {
			// no network, no need to try to reconnect
			// will try to reconnect when the browser goes back online
			return;
		}

		if (this.pendingReconnect) {
			return;
		}
		this.pendingReconnect = true;

		console.log("Trying to reconnect");
		if (immediate) {
			this._reconnectImpl(0, immediate);
			return;
		}

		this._reconnectImpl(Math.round(15000 + Math.random() * 1000));
	}

	_reconnectImpl(delay = 5000, immediate = false) {
		const onDone = (ws) => {
			if (ws?.readyState === WebSocket.OPEN) {
				console.log("WS is back online");
				this.onReconnect();
				this.pendingReconnect = false;
			}
		};

		if (immediate) {
			const ws = this.getWebSocket();

			// in case of connectivity loss, existing socket might stay in READY state for a long time, so we just force close it
			ws?.close(DT_MSG_CLIENT_RECREATE);
		}

		this._opening = false;
		// clear callbacks queue
		this._pendingCallbacks = [];
		setTimeout(() => {
			this.startSession(this.loadContext, onDone);
			if (this.reconnectOnOnline) {
				// after client went back online, we only try to reconect once
				clearInterval(this.reconnectOnOnline);
			}
		}, delay ?? 0);
	}
}


export class DtMsgHub {
	// we want to support at most two sockets, one for US and one for non-US region. Later one is supposed to be used only when a user is viewing a non-US facility.
	// if a socket has zero subscribers, it should be closed
	#sockets = [];

	constructor(loadContext, errorCB, sessionId, onReconnect = () => {}) {

		this.#sockets = endpoint.getSupportedRegions().map(region => {
			const ctx = {...loadContext};
			endpoint.switchRegion(ctx, region);
			return new DtMsgWs(ctx, errorCB, sessionId, onReconnect);
		});
	}

	get #socketsWithSubscribers() {
		return this.#sockets.filter(s => s.numSubscribers > 0);
	}

	#getSocket(loadContext) {
		return this.#sockets.find(s => s.msg_ws_endpoint === loadContext.msg_ws);
	}

	subscribeForChanges(loadContext, targetId, cb) {
		const socket = this.#getSocket(loadContext);
		const ws = socket.getWebSocket();
		if (ws) {
			// socket is ready, subscribe right away
			return socket.subscribeForChanges(targetId, cb);
		}

		// initiate a connection and subscribe when it's ready
		socket.startSession(loadContext, () => {
			socket.subscribeForChanges(targetId, cb);
		});
	}

	unsubscribeFromChanges(loadContext, targetId) {
		const socket = this.#getSocket(loadContext);

		if (socket.isSubscribed(targetId)) {
			socket.unsubscribeFromChanges(targetId);
			if (socket.numSubscribers === 0) {
				// no more subscribers, close the connection
				socket.endSession();
			}
		}
	}

	reconnect(immediate = false) {
		this.#socketsWithSubscribers.forEach(s => s.reconnect(immediate));
	}

	replayOnReconnect() {
		// TODO: there is a remote possibility that only one socket is to be replayed, but it's not handled here
		this.#socketsWithSubscribers.forEach(s => s.replayOnReconnect());
	}
}
